<template>
  <div class="request-creator">
    <h4
      id="order-products"
      class="mb-3 pb-3 border-bottom d-flex">
      {{ $t('product.products') }}

      <d-button
        v-if="!readOnly"
        theme="success"
        @click.prevent="addProduct"
        class="ml-auto">
        <i class="material-icons">add</i>
        {{ $t('product.addProduct') }}
      </d-button>
    </h4>

    <request-creator-product
      v-for="(productData, index) of productsData"
      :key="'product-' + index"
      :read-only="readOnly"
      :product-data="productData"
      :product-index="index"
      :currency-id="currencyID"
      :selected-currency-symbol="selectedCurrencySymbol"
      :products="products"
      :productsMinQty="productsMinQty"
      :productsDiscount="discountProducts"
      :partnerProductsDiscount="partnerDiscountProducts"
      :productsMarkup="markupProducts"
      :servicesMarkup="markupServices"
      :servicesSetupMarkup="markupSetupServices"
      :servicesDiscount="discountServices"
      :partnerServicesDiscount="partnerDiscountServices"
      :globalServices="servicesData"
      :isOrderView="isOrderView"
      :embroideryItems="embroideryItems"
      :services="services"
      :servicesGroups="servicesGroups"
      :whoOrders="whoOrders"
      :useCustomerPricing="useCustomerPricing"
      :orderSetUpInProgress="orderSetUpInProgress"
      :availabilityRefreshInProgress="availabilityRefreshInProgress" />

    <d-alert
      v-if="!productsData.length && !readOnly"
      show="true"
      theme="light"
      class="text-center">
      {{ $t('inquiries.addFirstProductInstructionInfo') }}
    </d-alert>

    <h4
      id="order-global-services"
      class="mt-5 mb-3 pb-3 border-bottom d-flex global-services-header">
      {{ $t('product.globalServices') }}

      <i
        id="global-services-tooltip"
        class="material-icons text-primary ml-2 mt-2"
        style="cursor: pointer;">
        help
      </i>

      <d-tooltip target="global-services-tooltip"
        container=".global-services-header">
        {{ $t('inquiries.globalServicesTooltip') }}
      </d-tooltip>

      <d-button
        v-if="!readOnly"
        theme="primary"
        @click.prevent="addGlobalService"
        class="ml-auto">
        <i class="material-icons">add</i>
        {{ $t('product.addGlobalService') }}
      </d-button>
    </h4>

    <request-creator-global-service
      v-for="(service, index) of servicesData"
      :key="'global-service-item-' + index"
      :currencyId="currencyID"
      :readOnly="readOnly"
      :serviceData="service"
      :selectedCurrencySymbol="selectedCurrencySymbol"
      :services="services"
      :servicesGroups="servicesGroups"
      :servicesDiscount="discountServices"
      :servicesMarkup="markupServices"
      :servicesSetupMarkup="markupSetupServices"
      :partnerServicesDiscount="partnerDiscountServices"
      :embroideryItems="embroideryItems"
      :whoOrders="whoOrders"
      :useCustomerPricing="useCustomerPricing"
      :orderSetUpInProgress="orderSetUpInProgress" />

    <d-alert
      v-if="!servicesData.length && !readOnly"
      show="true"
      theme="light"
      class="text-center">
      {{ $t('inquiries.addFirstServiceInstructionInfo') }}
    </d-alert>

    <h4
      id="order-summary"
      class="mt-5 mb-3 pb-3 border-bottom d-flex">
      {{ $t('order.summary') }}
    </h4>

    <d-row>
      <h5 class="text-right mt-0 mb-3 ml-auto mr-3 w-100">
        {{ $t('product.totalProductsCostNet') }}:

        <template v-if="whoOrders === 'me'">
          <strong class="text-primary">
            {{ roundProperly(totalProductsPrice.totalPrice) }}
            {{ selectedCurrencySymbol }}
          </strong>

          <template v-if="totalProductsPrice.totalPrice !== totalProductsPrice.totalPriceBeforeDiscount">
            <small class="d-block">
              {{ $t('product.totalBeforeDiscount') }}:
              <span>
                {{ totalProductsPrice.totalPriceBeforeDiscount }}
                {{ selectedCurrencySymbol }}
              </span>
            </small>
          </template>
        </template>

        <template v-if="whoOrders === 'my-customer'">
          <strong
            class="text-primary">
            {{ totalProductsPrice.totalPartnerPrice }}
            {{ selectedCurrencySymbol }}
          </strong>

          <template v-if="partnerDiscountProducts">
            <small class="d-block">
              {{ $t('product.totalBeforeDiscount') }}:
              <span>
                {{ roundProperly(parseFloat(totalProductsPrice.totalPartnerPriceBeforeDiscount)) }}
                {{ selectedCurrencySymbol }}
              </span>
            </small>
          </template>

          <small class="d-block">
            {{ $t('product.totalMarkup') }}:
            <span>
              {{ totalProductsPrice.totalMarkup }}
              {{ selectedCurrencySymbol }}
            </span>
          </small>
        </template>
      </h5>
    </d-row>

    <d-row>
      <h5 class="text-right mt-0 mb-3 ml-auto mr-3 w-100">
        {{ $t('product.totalServicesCostNet') }}:

        <template v-if="whoOrders === 'me'">
          <strong class="text-primary">
            {{ roundProperly(parseFloat(totalServicesPrice.totalPrice) + parseFloat(totalGlobalServicesPrice.totalPrice)) }}
            {{ selectedCurrencySymbol }}
          </strong>

          <template v-if="(parseFloat(totalServicesPrice.totalPrice) + parseFloat(totalGlobalServicesPrice.totalPrice)) !== (parseFloat(totalServicesPrice.totalPriceBeforeDiscount) + parseFloat(totalGlobalServicesPrice.totalPriceBeforeDiscount))">
            <small class="d-block">
              {{ $t('product.totalBeforeDiscount') }}:
              <span>
                {{ roundProperly(parseFloat(totalServicesPrice.totalPriceBeforeDiscount) + parseFloat(totalGlobalServicesPrice.totalPriceBeforeDiscount)) }}
                {{ selectedCurrencySymbol }}
              </span>
            </small>
          </template>
        </template>

        <template v-if="whoOrders === 'my-customer'">
          <strong class="text-primary">
            {{ roundProperly(parseFloat(totalServicesPrice.totalPartnerPrice) + parseFloat(totalGlobalServicesPrice.totalPartnerPrice)) }}
            {{ selectedCurrencySymbol }}
          </strong>

          <template v-if="partnerDiscountServices">
            <small class="d-block">
              {{ $t('product.totalBeforeDiscount') }}:
              <span>
                {{ roundProperly(parseFloat(totalServicesPrice.totalPartnerPriceBeforeDiscount) + parseFloat(totalGlobalServicesPrice.totalPartnerPriceBeforeDiscount)) }}
                {{ selectedCurrencySymbol }}
              </span>
            </small>
          </template>

          <small class="d-block">
            {{ $t('product.totalMarkup') }}:
            <span>
              {{ roundProperly(parseFloat(totalServicesPrice.totalMarkup) + parseFloat(totalGlobalServicesPrice.totalMarkup)) }}
              {{ selectedCurrencySymbol }}
            </span>
          </small>
        </template>
      </h5>
    </d-row>

    <d-row>
      <h5 class="text-right mt-0 mb-3 ml-auto mr-3 w-100">
        {{ $t('order.totalDeliveryCostNet') }}:

        <template v-if="whoOrders === 'me'">
          <strong class="text-primary">
            {{ roundProperly(shipperPrice) }}
            {{ selectedCurrencySymbol }}
          </strong>

          <template v-if="shipperPrice !== shipperPriceBeforeDiscount">
            <small class="d-block">
              {{ $t('product.totalBeforeDiscount') }}:
              <span>
                {{ roundProperly(shipperPriceBeforeDiscount) }}
                {{ selectedCurrencySymbol }}
              </span>
            </small>
          </template>
        </template>

        <template v-if="whoOrders === 'my-customer'">
          <strong class="text-primary">
            {{ roundProperly(partnerShipperPrice) }}
            {{ selectedCurrencySymbol }}
          </strong>

          <template v-if="partnerDiscountShipping">
            <small class="d-block">
              {{ $t('product.totalBeforeDiscount') }}:
              <span>
                {{ roundProperly(partnerShipperPriceBeforeDiscount) }}
                {{ selectedCurrencySymbol }}
              </span>
            </small>
          </template>

          <small class="d-block">
            {{ $t('product.totalMarkup') }}:
            <span>
              {{ roundProperly(partnerShipperPrice - shipperPrice) }}
              {{ selectedCurrencySymbol }}
            </span>
          </small>
        </template>

        <small
          v-if="!readOnly"
          class="mt-4 d-block">
          {{ $t('order.cartonsNumber') }}:
          <span class="text-primary">{{ boxesCount }} {{ $t('ui.piecesAbbr') }}</span>
          <br>
          <span
            v-if="boxesAlert"
            class="text-danger">
            {{ $t('order.cartonsMissingInfo') }}
          </span>
          <br v-if="boxesAlert">
          {{ $t('order.numberOfOrderdProducts') }}:
          <span class="text-primary">{{ productsCount }} {{ $t('ui.piecesAbbr') }}</span>
        </small>
      </h5>
    </d-row>

    <d-modal
      v-if="displaySamplePreviewPopup"
      @close="hideSamplePreviewPopup"
      size="lg">
      <d-modal-body class="p-0">
        <d-alert
          :show="!samplePreviewLoaded"
          theme="light"
          class="mt-3 text-center">
          {{ $t('ui.dataLoading') }}
        </d-alert>
        <img
          v-show="samplePreviewLoaded"
          :src="samplePreviewURL"
          @load="samplePreviewLoaded = true;"
          style="width: 100%; height: auto;" />
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-auto mr-2"
          theme="primary"
          @click.prevent="openInNewTab(samplePreviewURL)">
          {{ $t('ui.viewInNewTab') }}
        </d-button>

        <d-button
          class="mr-auto"
          theme="danger"
          @click.prevent="hideSamplePreviewPopup">
          {{ $t('ui.close') }}
        </d-button>
      </d-modal-footer>
    </d-modal>

    <products-search-popup
      v-if="!readOnly && productsSearchPopupIsOpen"
      :currency-id="currencyID"
      :selected-currency-symbol="selectedCurrencySymbol"
      :product-uuid="productsSearchPopupProductUUID"
      :tag-categories="tagCategories"
      :save-filters="true"
      :cartEnabled="false" />
  </div>
</template>

<script>
import ProductsSearchPopup from './ProductsSearchPopup.vue';
import RequestCreatorProduct from './RequestCreatorProduct.vue';
import RequestCreatorGlobalService from './RequestCreatorGlobalService.vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import debounce from 'lodash.debounce';
import Vue from 'vue';

export default {
  name: 'request-creator',
  props: {
    readOnly: {
      default: false,
      type: Boolean
    },
    currencies: {
      default: () => ([]),
      type: Array
    },
    currencyID: {
      default: 0,
      type: Number
    },
    products: {
      default: () => ([]),
      type: Array
    },
    services: {
      default: () => ([]),
      type: Array
    },
    servicesGroups: {
      default: () => ([]),
      type: Array
    },
    tagCategories: {
      default: () => ([]),
      type: Array
    },
    isOrderView: {
      default: false,
      type: Boolean
    },
    submittedFiles: {
      default: () => ([]),
      type: Array
    },
    discountProducts: {
      default: 0,
      type: Number
    },
    discountServices: {
      default: 0,
      type: Number
    },
    discountShipping: {
      default: 0,
      type: Number
    },
    partnerDiscountProducts: {
      default: 0,
      type: Number
    },
    partnerDiscountServices: {
      default: 0,
      type: Number
    },
    partnerDiscountShipping: {
      default: 0,
      type: Number
    },
    markupProducts: {
      default: 0,
      type: Number
    },
    markupServices: {
      default: 0,
      type: Number
    },
    markupSetupServices: {
      default: 0,
      type: Number
    },
    markupShipping: {
      default: 0,
      type: Number
    },
    whoOrders: {
      default: '',
      type: String
    },
    useCustomerPricing: {
      default: false,
      type: Boolean
    },
    embroideryItems: {
      default: () => ({}),
      type: Object
    },
    orderSetUpInProgress: {
      default: false,
      type: Boolean
    },
    cartData: {
      default: false,
      type: [Boolean, Array]
    },
    shipperPrice: {
      default: '',
      type: [String, Number]
    },
    shipperPriceBeforeDiscount: {
      default: '',
      type: [String, Number]
    },
    partnerShipperPrice: {
      default: '',
      type: [String, Number]
    },
    partnerShipperPriceBeforeDiscount: {
      default: '',
      type: [String, Number]
    },
    boxesAlert: {
      default: false,
      type: Boolean
    },
    boxesCount: {
      default: 0,
      type: Number
    },
    productsCount: {
      default: 0,
      type: Number
    }
  },
  components: {
    'products-search-popup': ProductsSearchPopup,
    'request-creator-product': RequestCreatorProduct,
    'request-creator-global-service': RequestCreatorGlobalService
  },
  computed: {
    productTotalPrices () {
      return this.discountProducts + '|' + this.markupProducts + '|' + this.productsData.map(product => ([product.partner_price, product.partner_price_before_discount, product.price, product.quantity].join(';'))).join('|');
    },
    servicesTotalPrices () {
      return this.discountServices + '|' + this.markupServices + '|' + this.markupSetupServices + '|' + this.productsData.map(product => product.quantity + ',' + product.services.map(service => [service.price, service.partner_price, service.partner_price_before_discount, service.price_multiple_from].join(';')).join(';') + ',' + product.services.map(service => [service.price_setup, service.partner_price_setup, service.partner_price_setup_before_discount].join(';')).join(';') + ',' + product.services.map(service => service.quantity).join(';')).join('|');
    },
    globalServicesTotalPrices () {
      return this.discountServices + '|' + this.markupServices + '|' + this.markupSetupServices + '|' + this.servicesData.map(service => [service.price, service.partner_price, service.partner_price_before_discount, service.price_setup, service.partner_price_setup, service.partner_price_setup_before_discount, service.quantity, service.price_multiple_from].join(';')).join('|');
    },
    totalProductsPrice () {
      let totalPrice = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPrice)))
        .map(product => parseFloat(product.totalPrice))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPartnerPrice = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPartnerPrice)))
        .map(product => parseFloat(product.totalPartnerPrice))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPriceBeforeDiscount = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPriceBeforeDiscount)))
        .map(product => parseFloat(product.totalPriceBeforeDiscount))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPartnerPriceBeforeDiscount = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPartnerPriceBeforeDiscount)))
        .map(product => parseFloat(product.totalPartnerPriceBeforeDiscount))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalMarkup = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalMarkup)))
        .map(product => parseFloat(product.totalMarkup))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      if (Number.isNaN(totalPrice)) {
        return {
          totalPrice: 0,
          totalPartnerPrice: 0,
          totalPriceBeforeDiscount: 0,
          totalPartnerPriceBeforeDiscount: 0,
          totalMarkup: 0
        };
      }

      return {
        totalPrice: CustomDecimals.roundProperly(totalPrice),
        totalPartnerPrice: CustomDecimals.roundProperly(totalPartnerPrice),
        totalPriceBeforeDiscount: CustomDecimals.roundProperly(totalPriceBeforeDiscount),
        totalPartnerPriceBeforeDiscount: CustomDecimals.roundProperly(totalPartnerPriceBeforeDiscount),
        totalMarkup: CustomDecimals.roundProperly(totalMarkup)
      };
    },
    totalServicesPrice () {
      let totalPrice = this.productsData
        .map(product => product.services
          .filter(service => !Number.isNaN(parseFloat(service.totalPrice)))
          .reduce((prev, service) => prev + parseFloat(service.totalPrice), 0))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPartnerPrice = this.productsData
        .map(product => product.services
          .filter(service => !Number.isNaN(parseFloat(service.totalPartnerPrice)))
          .reduce((prev, service) => prev + parseFloat(service.totalPartnerPrice), 0))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPriceBeforeDiscount = this.productsData
        .map(product => product.services
          .filter(service => !Number.isNaN(parseFloat(service.totalPriceBeforeDiscount)))
          .reduce((prev, service) => prev + parseFloat(service.totalPriceBeforeDiscount), 0))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalPartnerPriceBeforeDiscount = this.productsData
        .map(product => product.services
          .filter(service => !Number.isNaN(parseFloat(service.totalPartnerPriceBeforeDiscount)))
          .reduce((prev, service) => prev + parseFloat(service.totalPartnerPriceBeforeDiscount), 0))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      let totalMarkup = this.productsData
        .map(product => product.services
          .filter(service => !Number.isNaN(parseFloat(service.totalMarkup)))
          .reduce((prev, service) => prev + parseFloat(service.totalMarkup), 0))
        .reduce((prev, price) => prev + parseFloat(price), 0);

      if (Number.isNaN(totalPrice)) {
        return {
          totalPrice: 0,
          totalPartnerPrice: 0,
          totalPriceBeforeDiscount: 0,
          totalPartnerPriceBeforeDiscount: 0,
          totalMarkup: 0
        };
      }

      return {
        totalPrice: CustomDecimals.roundProperly(totalPrice),
        totalPartnerPrice: CustomDecimals.roundProperly(totalPartnerPrice),
        totalPriceBeforeDiscount: CustomDecimals.roundProperly(totalPriceBeforeDiscount),
        totalPartnerPriceBeforeDiscount: CustomDecimals.roundProperly(totalPartnerPriceBeforeDiscount),
        totalMarkup: CustomDecimals.roundProperly(totalMarkup)
      };
    },
    totalGlobalServicesPrice () {
      let totalPrice = this.servicesData
        .filter(service => !Number.isNaN(parseFloat(service.totalPrice)))
        .reduce((prev, service) => prev + parseFloat(service.totalPrice), 0);

      let totalPartnerPrice = this.servicesData
        .filter(service => !Number.isNaN(parseFloat(service.totalPartnerPrice)))
        .reduce((prev, service) => prev + parseFloat(service.totalPartnerPrice), 0);

      let totalPriceBeforeDiscount = this.servicesData
        .filter(service => !Number.isNaN(parseFloat(service.totalPriceBeforeDiscount)))
        .reduce((prev, service) => prev + parseFloat(service.totalPriceBeforeDiscount), 0);

      let totalPartnerPriceBeforeDiscount = this.servicesData
        .filter(service => !Number.isNaN(parseFloat(service.totalPartnerPriceBeforeDiscount)))
        .reduce((prev, service) => prev + parseFloat(service.totalPartnerPriceBeforeDiscount), 0);

      let totalMarkup = this.servicesData
        .filter(service => !Number.isNaN(parseFloat(service.totalMarkup)))
        .reduce((prev, service) => prev + parseFloat(service.totalMarkup), 0);

      if (Number.isNaN(totalPrice)) {
        return {
          totalPrice: 0,
          totalPartnerPrice: 0,
          totalPriceBeforeDiscount: 0,
          totalPartnerPriceBeforeDiscount: 0,
          totalMarkup: 0
        };
      }

      return {
        totalPrice: CustomDecimals.roundProperly(totalPrice),
        totalPartnerPrice: CustomDecimals.roundProperly(totalPartnerPrice),
        totalPriceBeforeDiscount: CustomDecimals.roundProperly(totalPriceBeforeDiscount),
        totalPartnerPriceBeforeDiscount: CustomDecimals.roundProperly(totalPartnerPriceBeforeDiscount),
        totalMarkup: CustomDecimals.roundProperly(totalMarkup)
      };
    },
    selectedCurrencySymbol () {
      let foundedCurrency = this.currencies.filter(currency => currency.id === this.currencyID);

      if (foundedCurrency.length) {
        return foundedCurrency[0].code;
      }

      return '';
    },
    totalProductItemsCount () {
      return this.productsData.reduce((prev, product) => prev + parseInt(product.quantity, 10), 0);
    },
    orderProductIDs () {
      return this.productsData.reduce((prev, product) => prev + ';' + parseInt(product.product_id, 10), '');
    },
    productsMinQty () {
      let output = {};

      for (let i = 0; i < this.products.length; i++) {
        let product = this.products[i];
        output[product.id] = product.minimum_quantity;
      }

      return output;
    }
  },
  watch: {
    totalProductItemsCount (newValue, oldValue) {
      if (
        (oldValue > 0 && newValue === 0) ||
        (oldValue === 0 && newValue > 0) ||
        (oldValue <= 25 && newValue > 25) ||
        (oldValue > 25 && newValue <= 25)
      ) {
        this.$bus.$emit('order-check-shipping-price');
      }

      this.$bus.$emit('order-check-shipping-price');
    },
    orderProductIDs () {
      this.$bus.$emit('order-check-shipping-price');
      this.refreshProductsAvailability();
    },
    productsData (newValue) {
      if (newValue.length === 0) {
        this.addServices = false;
        this.offerServicesGroups = [];
      }
    },
    productTotalPrices () {
      for (let i = 0; i < this.productsData.length; i++) {
        if (!Number.isNaN(parseFloat(this.productsData[i].price))) {
          Vue.set(this.productsData[i], 'totalPrice', CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].price)));
          Vue.set(this.productsData[i], 'totalPartnerPrice', CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].partner_price)));
          Vue.set(this.productsData[i], 'totalPriceBeforeDiscount', CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].price_before_discount)));
          Vue.set(this.productsData[i], 'totalPartnerPriceBeforeDiscount', CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].partner_price_before_discount)));
          Vue.set(this.productsData[i], 'totalMarkup', CustomDecimals.roundProperly(this.productsData[i].quantity * parseFloat(this.productsData[i].partner_price - this.productsData[i].price)));
        }
      }
    },
    servicesTotalPrices () {
      Vue.nextTick(() => {
        for (let i = 0; i < this.productsData.length; i++) {
          let services = this.productsData[i].services;

          for (let j = 0; j < services.length; j++) {
            let setupPrice = parseFloat(this.productsData[i].services[j].price_setup);
            let setupPriceBeforeDiscount = parseFloat(this.productsData[i].services[j].price_setup_before_discount);
            let setupPartnerPrice = parseFloat(this.productsData[i].services[j].partner_price_setup);
            let setupPartnerPriceBeforeDiscount = parseFloat(this.productsData[i].services[j].partner_price_setup_before_discount);
            let itemPrice = parseFloat(this.productsData[i].services[j].price);
            let itemPriceBeforeDiscount = parseFloat(this.productsData[i].services[j].price_before_discount);
            let itemPartnerPrice = parseFloat(this.productsData[i].services[j].partner_price);
            let itemPartnerPriceBeforeDiscount = parseFloat(this.productsData[i].services[j].partner_price_before_discount);
            let quantity = parseInt(this.productsData[i].services[j].quantity, 10);
            let serviceMultipleFrom = parseInt(this.productsData[i].services[j].price_multiple_from, 10);

            if (!Number.isNaN(itemPrice) && !Number.isNaN(setupPrice)) {
              let totalPrice = itemPrice + setupPrice;
              let totalPartnerPrice = itemPartnerPrice + setupPartnerPrice;
              let totalPriceBeforeDiscount = itemPriceBeforeDiscount + setupPriceBeforeDiscount;
              let totalPartnerPriceBeforeDiscount = itemPartnerPriceBeforeDiscount + setupPartnerPriceBeforeDiscount;
              let totalMarkup = (itemPartnerPrice - itemPrice) + (setupPartnerPrice - setupPrice);

              if (quantity >= serviceMultipleFrom) {
                totalPrice = (quantity * itemPrice) + setupPrice;
                totalPartnerPrice = (quantity * itemPartnerPrice) + setupPartnerPrice;
                totalPriceBeforeDiscount = (quantity * itemPriceBeforeDiscount) + setupPriceBeforeDiscount;
                totalPartnerPriceBeforeDiscount = (quantity * itemPartnerPriceBeforeDiscount) + setupPartnerPriceBeforeDiscount;
                totalMarkup = (quantity * (itemPartnerPrice - itemPrice)) + (setupPartnerPrice - setupPrice);
              }

              Vue.set(this.productsData[i].services[j], 'totalPrice', CustomDecimals.roundProperly(totalPrice));
              Vue.set(this.productsData[i].services[j], 'totalPartnerPrice', CustomDecimals.roundProperly(totalPartnerPrice));
              Vue.set(this.productsData[i].services[j], 'totalPartnerPriceBeforeDiscount', CustomDecimals.roundProperly(totalPartnerPriceBeforeDiscount));
              Vue.set(this.productsData[i].services[j], 'totalPriceBeforeDiscount', CustomDecimals.roundProperly(totalPriceBeforeDiscount));
              Vue.set(this.productsData[i].services[j], 'totalMarkup', CustomDecimals.roundProperly(totalMarkup));
            }
          }
        }
      });
    },
    globalServicesTotalPrices () {
      Vue.nextTick(() => {
        for (let i = 0; i < this.servicesData.length; i++) {
          let setupPrice = parseFloat(this.servicesData[i].price_setup);
          let setupPriceBeforeDiscount = parseFloat(this.servicesData[i].price_setup_before_discount);
          let setupPartnerPrice = parseFloat(this.servicesData[i].partner_price_setup);
          let setupPartnerPriceBeforeDiscount = parseFloat(this.servicesData[i].partner_price_setup_before_discount);
          let itemPrice = parseFloat(this.servicesData[i].price);
          let itemPriceBeforeDiscount = parseFloat(this.servicesData[i].price_before_discount);
          let itemPartnerPrice = parseFloat(this.servicesData[i].partner_price);
          let itemPartnerPriceBeforeDiscount = parseFloat(this.servicesData[i].partner_price_before_discount);
          let quantity = parseInt(this.servicesData[i].quantity, 10);
          let serviceMultipleFrom = parseInt(this.servicesData[i].price_multiple_from, 10);

          if (!Number.isNaN(itemPrice) && !Number.isNaN(setupPrice)) {
            let totalPrice = itemPrice + setupPrice;
            let totalPartnerPrice = itemPartnerPrice + setupPartnerPrice;
            let totalPriceBeforeDiscount = itemPriceBeforeDiscount + setupPriceBeforeDiscount;
            let totalPartnerPriceBeforeDiscount = itemPartnerPriceBeforeDiscount + setupPartnerPriceBeforeDiscount;
            let totalMarkup = (itemPartnerPrice - itemPrice) + (setupPartnerPrice - setupPrice);

            if (quantity >= serviceMultipleFrom) {
              totalPrice = (quantity * itemPrice) + setupPrice;
              totalPartnerPrice = (quantity * itemPartnerPrice) + setupPartnerPrice;
              totalPriceBeforeDiscount = (quantity * itemPriceBeforeDiscount) + setupPriceBeforeDiscount;
              totalPartnerPriceBeforeDiscount = (quantity * itemPartnerPriceBeforeDiscount) + setupPartnerPriceBeforeDiscount;
              totalMarkup = (quantity * (itemPartnerPrice - itemPrice)) + (setupPartnerPrice - setupPrice);
            }

            Vue.set(this.servicesData[i], 'totalPrice', CustomDecimals.roundProperly(totalPrice));
            Vue.set(this.servicesData[i], 'totalPartnerPrice', CustomDecimals.roundProperly(totalPartnerPrice));
            Vue.set(this.servicesData[i], 'totalPriceBeforeDiscount', CustomDecimals.roundProperly(totalPriceBeforeDiscount));
            Vue.set(this.servicesData[i], 'totalPartnerPriceBeforeDiscount', CustomDecimals.roundProperly(totalPartnerPriceBeforeDiscount));
            Vue.set(this.servicesData[i], 'totalMarkup', CustomDecimals.roundProperly(totalMarkup));
          }
        }
      });
    }
  },
  data () {
    return {
      displaySamplePreviewPopup: false,
      productsData: [],
      productsSearchPopupIsOpen: false,
      productsSearchPopupProductUUID: false,
      samplePreviewURL: '',
      samplePreviewLoaded: false,
      servicesData: [],
      priceChanges: [],
      availabilityRefreshInProgress: false
    };
  },
  mounted () {
    this.$bus.$on('products-search-popup-select-product', this.setProduct);
    this.$bus.$on('request-creator-duplicate-product', this.duplicateProduct);
    this.$bus.$on('request-creator-remove-product', this.removeProduct);
    this.$bus.$on('request-creator-add-service', this.addService);
    this.$bus.$on('request-creator-duplicate-service', this.duplicateService);
    this.$bus.$on('request-creator-remove-service', this.removeService);
    this.$bus.$on('request-creator-add-global-service', this.addGlobalService);
    this.$bus.$on('request-creator-duplicate-global-service', this.duplicateGlobalService);
    this.$bus.$on('request-creator-remove-global-service', this.removeGlobalService);
    this.$bus.$on('products-search-popup-open', this.openProductsSearchPopup);
    this.$bus.$on('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$on('request-creator-update-product-fields', this.updateProductFields);
    this.$bus.$on('request-creator-update-service-fields', this.updateServiceFields);
    this.$bus.$on('request-creator-update-global-service-fields', this.updateGlobalServiceFields);
    this.$bus.$on('request-creator-initial-price-check', this.collectPriceChanges);
    this.$bus.$on('request-show-sample-preview', this.showSamplePreviewPopup);

    Vue.nextTick(() => {
      if (this.$route.path === '/zamowienia/nowe/z-koszyka') {
        this.parseCartData();
      }
    });
  },
  methods: {
    addProduct () {
      let generatedUUID = this.$generateUUID();

      this.productsData.push({
        uuid: generatedUUID,
        product_id: null,
        additionalData: {},
        quantity: 1,
        price: '',
        price_before_discount: '',
        partner_price: '',
        partner_price_before_discount: '',
        totalPrice: '',
        customer_description: '',
        hasDescription: false,
        services: [],
        global_services: {}
      });

      Vue.nextTick(() => {
        document.getElementById('product-' + generatedUUID).scrollIntoView();
      });
    },
    addService (productUUID) {
      for (let i = 0; i < this.productsData.length; i++) {
        if (this.productsData[i].uuid === productUUID) {
          this.productsData[i].services.push({
            uuid: this.$generateUUID(),
            product_id: null,
            service_id: null,
            serviceData: {},
            quantity: 1,
            price: '',
            price_before_discount: '',
            price_setup: '',
            price_setup_before_discount: '',
            partner_price: '',
            partner_price_before_discount: '',
            partner_price_setup: '',
            partner_price_setup_before_discount: '',
            totalPrice: ''
          });
        }
      }
    },
    addGlobalService () {
      let generatedUUID = this.$generateUUID();
      let temporaryID = +new Date();

      this.servicesData.push({
        uuid: generatedUUID,
        temp_id: temporaryID,
        product_id: null,
        service_id: null,
        serviceData: {},
        quantity: 1,
        price: '',
        price_before_discount: '',
        price_setup: '',
        price_setup_before_discount: '',
        partner_price: '',
        partner_price_before_discount: '',
        partner_price_setup: '',
        partner_price_setup_before_discount: '',
        totalPrice: ''
      });

      /*
      for (let i = 0; i < this.productsData.length; i++) {
        this.productsData[i].global_services[temporaryID] = 0;
      }
      */

      Vue.nextTick(() => {
        document.getElementById('global-service-' + generatedUUID).scrollIntoView();
      });
    },
    setProduct (data) {
      for (let i = 0; i < this.productsData.length; i++) {
        if (this.productsData[i].uuid === data.uuid) {
          Vue.set(this.productsData[i], 'product_id', data.id);
        }
      }
    },
    setProductFromCart (index, productID, qty) {
      if (this.productsData[index]) {
        Vue.set(this.productsData[index], 'product_id', productID);
        Vue.set(this.productsData[index], 'quantity', qty);
      }
    },
    duplicateProduct (productUUID) {
      let productToDuplicate = this.productsData.filter(product => product.uuid === productUUID);

      if (productToDuplicate && productToDuplicate.length) {
        let duplicatedProduct = JSON.parse(JSON.stringify(productToDuplicate[0]));
        duplicatedProduct.id = null;
        duplicatedProduct.uuid = this.$generateUUID();

        if (duplicatedProduct.services && duplicatedProduct.services.length) {
          for (let i = 0; i < duplicatedProduct.services.length; i++) {
            duplicatedProduct.services[i].id = null;
            duplicatedProduct.services[i].uuid = this.$generateUUID();
            duplicatedProduct.services[i].is_duplicated = true;
            this.resetDuplicatedServiceFiles(duplicatedProduct.services[i]);
          }
        }

        this.productsData.push(duplicatedProduct);
      }
    },
    resetDuplicatedServiceFiles (service) {
      service.files = [];
      service.sample_images = [];
      delete service.fileToUpload;
    },
    removeProduct (productUUID) {
      let updatedProducts = this.productsData.filter(product => product.uuid !== productUUID);
      Vue.set(this, 'productsData', updatedProducts);
    },
    duplicateService (productUUID, serviceUUID) {
      for (let i = 0; i < this.productsData.length; i++) {
        let product = this.productsData[i];

        if (product.uuid === productUUID) {
          for (let j = 0; j < product.services.length; j++) {
            let service = this.productsData[i].services[j];

            if (service.uuid === serviceUUID) {
              let duplicatedService = JSON.parse(JSON.stringify(service));
              duplicatedService.uuid = this.$generateUUID();
              duplicatedService.id = null;
              duplicatedService.is_duplicated = true;
              this.resetDuplicatedServiceFiles(duplicatedService);
              this.productsData[i].services.push(duplicatedService);
            }
          }

          break;
        }
      }
    },
    duplicateGlobalService (serviceUUID) {
      let serviceToDuplicate = this.servicesData.filter(service => service.uuid === serviceUUID);

      if (serviceToDuplicate && serviceToDuplicate.length) {
        let duplicatedService = JSON.parse(JSON.stringify(serviceToDuplicate[0]));
        duplicatedService.id = null;
        duplicatedService.uuid = this.$generateUUID();
        duplicatedService.is_duplicated = true;
        this.resetDuplicatedServiceFiles(duplicatedService);
        this.servicesData.push(duplicatedService);
      }
    },
    removeService (productUUID, serviceUUID) {
      for (let i = 0; i < this.productsData.length; i++) {
        let product = this.productsData[i];

        if (product.uuid === productUUID) {
          let updatedServices = product.services.filter(service => service.uuid !== serviceUUID);
          Vue.set(this.productsData[i], 'services', updatedServices);
        }
      }
    },
    removeGlobalService (serviceUUID) {
      let serviceToFind = this.servicesData.filter(service => service.uuid === serviceUUID);

      if (serviceToFind[0]) {
        let serviceID = serviceToFind[0].id;

        if (serviceToFind[0].temp_id) {
          serviceID = serviceToFind[0].temp_id;
        }

        for (let i = 0; i < this.productsData.length; i++) {
          if (this.productsData[i].global_services && this.productsData[i].global_services[serviceID]) {
            Vue.delete(this.productsData[i].global_services, serviceID);
          }
        }
      }

      let updatedServices = this.servicesData.filter(service => service.uuid !== serviceUUID);
      Vue.set(this, 'servicesData', updatedServices);
    },
    prepareProductsData () {
      return this.productsData.map(product => {
        let preparedGlobalServices = [];

        if (product.global_services) {
          let globalServicesIDs = Object.keys(product.global_services);

          for (let i = 0; i < globalServicesIDs.length; i++) {
            let foundedService = this.servicesData.filter(service => service.temp_id === parseInt(globalServicesIDs[i], 10));

            if (foundedService[0]) {
              preparedGlobalServices.push({
                new_global_service_id: globalServicesIDs[i],
                quantity: product.global_services[globalServicesIDs[i]]
              });
            } else {
              preparedGlobalServices.push({
                order_service_id: globalServicesIDs[i],
                quantity: product.global_services[globalServicesIDs[i]]
              });
            }
          }
        }

        let productDataToSave = {
          product_id: product.product_id,
          partner_price: CustomDecimals.roundProperly(parseFloat(product.partner_price)),
          price: CustomDecimals.roundProperly(parseFloat(product.price)),
          price_before_discount: CustomDecimals.roundProperly(parseFloat(product.price_before_discount)),
          use_custom_partner_price: this.whoOrders === 'my-customer' ? product.use_custom_partner_price : 0,
          quantity: product.quantity,
          include_discounts: product.include_discounts,
          description: product.description,
          global_services: preparedGlobalServices,
          services: product.services.map(service => {
            let serviceDataToSave = {
              id: service.id,
              service_id: service.service_id,
              quantity: service.quantity,
              price_multiple_from: service.price_multiple_from,
              price: CustomDecimals.roundProperly(service.price),
              price_before_discount: CustomDecimals.roundProperly(service.price_before_discount),
              price_setup: CustomDecimals.roundProperly(service.price_setup),
              price_setup_before_discount: CustomDecimals.roundProperly(service.price_setup_before_discount),
              partner_price: CustomDecimals.roundProperly(parseFloat(service.partner_price)),
              partner_price_setup: CustomDecimals.roundProperly(parseFloat(service.partner_price_setup)),
              use_custom_partner_price: this.whoOrders === 'my-customer' ? service.use_custom_partner_price : 0,
              use_custom_setup_partner_price: this.whoOrders === 'my-customer' ? service.use_custom_setup_partner_price : 0,
              embroidery_type_id: service.embroidery_type_id ? service.embroidery_type_id : 0,
              embroidery_position_id: service.embroidery_position_id ? service.embroidery_position_id : 0,
              embroidery_width: service.embroidery_width,
              embroidery_height: service.embroidery_height,
              embroidery_color: service.embroidery_color
            };

            if (this.whoOrders === 'my-customer') {
              serviceDataToSave.partner_price_before_discount = CustomDecimals.roundProperly(parseFloat(service.partner_price_before_discount));
              serviceDataToSave.partner_price_setup_before_discount = CustomDecimals.roundProperly(parseFloat(service.partner_price_setup_before_discount));
            }

            return serviceDataToSave;
          })
        };

        productDataToSave.partner_price_before_discount = CustomDecimals.roundProperly(parseFloat(product.partner_price_before_discount));

        return productDataToSave;
      });
    },
    prepareServicesData () {
      return this.servicesData.map(service => {
        let serviceDataToSave = {
          id: service.id,
          service_id: service.service_id,
          quantity: service.quantity,
          price_multiple_from: service.price_multiple_from,
          price: CustomDecimals.roundProperly(service.price),
          price_before_discount: CustomDecimals.roundProperly(service.price_before_discount),
          price_setup: CustomDecimals.roundProperly(service.price_setup),
          price_setup_before_discount: CustomDecimals.roundProperly(service.price_setup_before_discount),
          partner_price: CustomDecimals.roundProperly(parseFloat(service.partner_price)),
          partner_price_setup: CustomDecimals.roundProperly(parseFloat(service.partner_price_setup)),
          use_custom_partner_price: this.whoOrders === 'my-customer' ? service.use_custom_partner_price : 0,
          use_custom_setup_partner_price: this.whoOrders === 'my-customer' ? service.use_custom_setup_partner_price : 0,
          embroidery_type_id: service.embroidery_type_id ? service.embroidery_type_id : 0,
          embroidery_position_id: service.embroidery_position_id ? service.embroidery_position_id : 0,
          embroidery_width: service.embroidery_width,
          embroidery_height: service.embroidery_height,
          embroidery_color: service.embroidery_color
        };

        if (this.whoOrders === 'my-customer') {
          serviceDataToSave.partner_price_before_discount = CustomDecimals.roundProperly(parseFloat(service.partner_price_before_discount));
          serviceDataToSave.partner_price_setup_before_discount = CustomDecimals.roundProperly(parseFloat(service.partner_price_setup_before_discount));
        }

        if (service.temp_id) {
          serviceDataToSave.new_global_service_id = service.temp_id;
        }

        return serviceDataToSave;
      });
    },
    openProductsSearchPopup (productUUID) {
      this.productsSearchPopupIsOpen = true;
      this.productsSearchPopupProductUUID = productUUID;
    },
    closeProductsSearchPopup () {
      this.productsSearchPopupIsOpen = false;
    },
    setUpProducts (productsData) {
      this.productsData = productsData.map(product => {
        let preparedGlobalServices = {};

        if (product.global_services) {
          for (let a = 0; a < product.global_services.length; a++) {
            let id = product.global_services[a].order_service_id;
            let qty = product.global_services[a].quantity;
            preparedGlobalServices[id] = qty;
          }
        }

        product.uuid = this.$generateUUID();
        product.global_services = preparedGlobalServices;

        product.services = product.services.map(service => {
          service.uuid = this.$generateUUID();
          service.price = Number(service.price).toFixed(2);
          service.price_before_discount = Number(service.price_before_discount).toFixed(2);
          service.price_setup = Number(service.price_setup).toFixed(2);
          service.price_setup_before_discount = Number(service.price_setup_before_discount).toFixed(2);
          service.partner_price = Number(service.partner_price).toFixed(2);
          service.partner_price_setup = Number(service.partner_price_setup).toFixed(2);
          service.partner_price_before_discount = Number(service.partner_price_before_discount).toFixed(2);
          service.partner_price_setup_before_discount = Number(service.partner_price_setup_before_discount).toFixed(2);
          service.serviceData = {
            uses_embroidery: service.uses_embroidery,
            embriodery_id: service.embriodery_id,
            dst_number: service.dst_number,
            stitch_number: service.stitch_number,
            strand_colors: service.strand_colors,
            prices: this.getServicePricesByID(service.service_id, 'prices'),
            customer_prices: this.getServicePricesByID(service.service_id, 'customer_prices')
          };

          return service;
        });

        product.additionalData = {
          prices: this.getProductPricesByID(product.product_id, 'prices'),
          customer_prices: this.getProductPricesByID(product.product_id, 'customer_prices')
        };

        return product;
      });
    },
    orderProductIDsForAvailability () {
      return [...new Set(this.productsData.map(product => parseInt(product.product_id, 10)).filter(id => !Number.isNaN(id)))];
    },
    refreshProductsAvailability () {
      if (!this.orderProductIDsForAvailability().length) {
        return;
      }

      if (this.readOnly) {
        return;
      }

      this.availabilityRefreshInProgress = true;
      this.debouncedFetchProductsAvailability();
    },
    debouncedFetchProductsAvailability: debounce(function () {
      let productIDs = this.orderProductIDsForAvailability();

      if (!productIDs.length) {
        this.availabilityRefreshInProgress = false;
        return;
      }

      this.$http.get('/customer-panel/products/availability', {
        params: {
          ids: productIDs
        }
      }).then(response => {
        let availabilityData = response.data.data;

        for (let i = 0; i < availabilityData.length; i++) {
          let item = availabilityData[i];

          for (let j = 0; j < this.products.length; j++) {
            if (parseInt(this.products[j].id, 10) === parseInt(item.id, 10)) {
              Vue.set(this.products[j], 'availability', item.availability);
            }
          }
        }

        this.availabilityRefreshInProgress = false;
      }).catch(() => {
        this.availabilityRefreshInProgress = false;
      });
    }, 500),
    setUpServices (servicesData) {
      this.servicesData = servicesData.map(service => {
        service.uuid = this.$generateUUID();
        service.price = Number(service.price).toFixed(2);
        service.price_before_discount = Number(service.price_before_discount).toFixed(2);
        service.price_setup = Number(service.price_setup).toFixed(2);
        service.price_setup_before_discount = Number(service.price_setup_before_discount).toFixed(2);
        service.partner_price = Number(service.partner_price).toFixed(2);
        service.partner_price_setup = Number(service.partner_price_setup).toFixed(2);
        service.partner_price_before_discount = Number(service.partner_price_before_discount).toFixed(2);
        service.partner_price_setup_before_discount = Number(service.partner_price_setup_before_discount).toFixed(2);
        service.serviceData = {
          uses_embroidery: service.uses_embroidery,
          embriodery_id: service.embriodery_id,
          dst_number: service.dst_number,
          stitch_number: service.stitch_number,
          strand_colors: service.strand_colors,
          prices: this.getServicePricesByID(service.service_id, 'prices'),
          customer_prices: this.getServicePricesByID(service.service_id, 'customer_prices')
        };

        return service;
      });
    },
    updateProductFields (data) {
      console.log('PRODUCT UPDATE', data);
      let uuid = data.uuid;
      let fields = data.fields;

      for (let i = 0; i < this.productsData.length; i++) {
        let product = this.productsData[i];

        if (product.uuid === uuid) {
          let fieldsToUpdate = Object.keys(fields);

          for (let j = 0; j < fieldsToUpdate.length; j++) {
            let fieldName = fieldsToUpdate[j];
            Vue.set(this.productsData[i], fieldName, fields[fieldName]);
          }
        }
      }
    },
    updateServiceFields (data) {
      console.log('SERVICE UPDATE', data);
      let productUUID = data.product_uuid;
      let serviceUUID = data.service_uuid;
      let fields = data.fields;

      for (let i = 0; i < this.productsData.length; i++) {
        let product = this.productsData[i];

        if (product.uuid === productUUID) {
          for (let j = 0; j < product.services.length; j++) {
            let service = product.services[j];

            if (service.uuid === serviceUUID) {
              let fieldsToUpdate = Object.keys(fields);

              for (let k = 0; k < fieldsToUpdate.length; k++) {
                let fieldName = fieldsToUpdate[k];
                Vue.set(this.productsData[i].services[j], fieldName, fields[fieldName]);
              }
            }
          }
        }
      }
    },
    updateGlobalServiceFields (data) {
      console.log('GLOBAL SERVICE UPDATE', data);
      let uuid = data.service_uuid;
      let fields = data.fields;

      for (let i = 0; i < this.servicesData.length; i++) {
        let service = this.servicesData[i];

        if (service.uuid === uuid) {
          let fieldsToUpdate = Object.keys(fields);

          for (let j = 0; j < fieldsToUpdate.length; j++) {
            let fieldName = fieldsToUpdate[j];
            Vue.set(this.servicesData[i], fieldName, fields[fieldName]);
          }
        }
      }
    },
    getServicePricesByID (serviceID, pricingType) {
      let foundedService = this.services.filter(service => service.id === serviceID);

      if (foundedService.length) {
        return foundedService[0][pricingType];
      }

      return [];
    },
    getProductPricesByID (productID, pricingType) {
      let foundedProduct = this.products.filter(product => product.id === productID);

      if (foundedProduct.length) {
        return foundedProduct[0][pricingType];
      }

      return [];
    },
    roundProperly (value) {
      return CustomDecimals.roundProperly(value);
    },
    parseCartData () {
      for (let i = 0; i < this.cartData.length; i++) {
        this.addProduct();

        Vue.nextTick(() => {
          this.setProductFromCart(i, this.cartData[i].product_id, this.cartData[i].qty);
        });
      }

      if (this.cartData.length) {
        Vue.swal({
          title: this.$t('order.addedProducts'),
          html: this.cartData.map(cartItem => '<small class="d-block" style="line-height: 1.3;">' + cartItem.product_name + '<strong class="text-primary"> &times;&nbsp;' + cartItem.qty + '&nbsp;' + this.$t('ui.piecesAbbr') + '</strong></small>').join(''),
          type: 'info',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }
    },
    cartonsAmount () {
      let sum = 0;
      let foundProductWithoutPiecesInCartoonSet = false;

      for (let i = 0; i < this.productsData.length; i++) {
        let productID = this.productsData[i].product_id;
        let qty = this.productsData[i].quantity;
        let foundedProduct = this.products.filter(product => product.id === productID);

        if (foundedProduct[0]) {
          let piecesInCarton = parseInt(foundedProduct[0].real_pieces_in_carton, 10);

          if (piecesInCarton) {
            let amount = qty / piecesInCarton;
            sum += amount;
          } else {
            foundProductWithoutPiecesInCartoonSet = true;
          }
        }
      }

      return {
        amount: Math.ceil(sum),
        productsWithoutPieces: foundProductWithoutPiecesInCartoonSet
      };
    },
    checkPricesInOrder () {
      this.$bus.$emit('request-creator-global-initial-product-update-price');
      this.$bus.$emit('request-creator-global-initial-services-update-price');

      setTimeout(() => {
        if (this.priceChanges.length) {
          this.$bus.$emit('request-creator-show-price-changes-popup', this.priceChanges);
        }
      }, 1000);
    },
    collectPriceChanges (priceChange) {
      this.priceChanges.push(priceChange);
    },
    showSamplePreviewPopup (url) {
      this.samplePreviewLoaded = false;
      this.samplePreviewURL = url;

      Vue.nextTick(() => {
        this.displaySamplePreviewPopup = true;
      });
    },
    hideSamplePreviewPopup () {
      this.displaySamplePreviewPopup = false;
      this.samplePreviewURL = '';
      this.samplePreviewLoaded = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('products-search-popup-select-product', this.setProduct);
    this.$bus.$off('request-creator-duplicate-product', this.duplicateProduct);
    this.$bus.$off('request-creator-remove-product', this.removeProduct);
    this.$bus.$off('request-creator-add-service', this.addService);
    this.$bus.$off('request-creator-duplicate-service', this.duplicateService);
    this.$bus.$off('request-creator-remove-service', this.removeService);
    this.$bus.$off('request-creator-add-global-service', this.addGlobalService);
    this.$bus.$off('request-creator-duplicate-global-service', this.duplicateGlobalService);
    this.$bus.$off('request-creator-remove-global-service', this.removeGlobalService);
    this.$bus.$off('products-search-popup-open', this.openProductsSearchPopup);
    this.$bus.$off('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$off('request-creator-update-product-fields', this.updateProductFields);
    this.$bus.$off('request-creator-update-service-fields', this.updateServiceFields);
    this.$bus.$off('request-creator-update-global-service-fields', this.updateGlobalServiceFields);
    this.$bus.$off('request-creator-initial-price-check', this.collectPriceChanges);
    this.$bus.$off('request-show-sample-preview', this.showSamplePreviewPopup);
  }
}
</script>
